export type MealType = "breakfast" | "lunch" | "dinner" | "snack";

export const MEAL_TYPES: MealType[] = ["breakfast", "lunch", "dinner", "snack"];

export const MEAL_TYPE_LABELS: Record<MealType, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks"
};

export const MEAL_TYPE_ICONS: Record<MealType, string> = {
  breakfast: "🌅",
  lunch: "☀️",
  dinner: "🌙",
  snack: "🍪"
};

export function inferMealType(date: Date): MealType {
  const h = date.getHours();
  if (h >= 4 && h < 10) return "breakfast";
  if (h >= 10 && h < 14) return "lunch";
  if (h >= 17 && h < 21) return "dinner";
  return "snack";
}

import type { NutrientPanel } from "./nutrients";
import { scaleNutrients } from "./nutrients";
import { scalePortionText } from "./portions";
import type { FoodCategory } from "./portions";

export interface FoodItem {
  name: string;
  portion: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g?: number;
  /** Gram weight of ONE portion as defined by `portion`. Powers cross-
   *  category unit switching in the wheel picker — without this we can
   *  only convert within volume↔volume or mass↔mass. Returned by the AI
   *  estimator and back-filled from generic-foods catalog entries that
   *  carry explicit `units[]` data. Falls back to null if unknown; the
   *  UI degrades gracefully. */
  grams_per_portion?: number;
  /** Coarse food category used to pick a density estimate for volume↔mass
   *  conversion ("dairy_thick" ≈ 1.04 g/ml, "oil" ≈ 0.92, etc.). The AI
   *  returns one of the FoodCategory values from lib/portions.ts when it
   *  can identify the food's broad type; otherwise we fall back to
   *  guessFoodCategory(name) or "other" (density 1.0). */
  food_category?: FoodCategory;
  /** Optional 25-nutrient panel (vitamins + minerals + sugar + sat fat).
      Populated by the AI estimator + Open Food Facts when available. */
  nutrients?: NutrientPanel;
}

export interface MealEstimate {
  items: FoodItem[];
  totals: {
    calories: number;
    protein_g: number;
    carbs_g: number;
    fat_g: number;
    fiber_g: number;
  };
  confidence: "low" | "medium" | "high";
  clarifying_questions: string[];
  notes?: string;
}

export interface LoggedMeal {
  id: string;
  user_id: string;
  logged_at: string;
  meal_type: MealType;
  photo_data_url?: string;
  caption?: string;
  estimate: MealEstimate;
  source_providers: string[];
  disagreement_pct?: number;
}

export function emptyTotals(): MealEstimate["totals"] {
  return { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 };
}

export function scaleEstimate(estimate: MealEstimate, quantity: number): MealEstimate {
  if (quantity === 1) return estimate;
  return {
    ...estimate,
    items: estimate.items.map((it) => ({
      ...it,
      // Scale the portion label too so the saved meal doesn't say "1 large
      // egg" when the user logged 2. scalePortionText handles "1 cup" →
      // "2 cups", "1 large (~50g)" → "2 large (~100g)", and falls back to
      // an " × N" suffix only when nothing else parses.
      portion: scalePortionText(it.portion, quantity),
      calories: round1(it.calories * quantity),
      protein_g: round1(it.protein_g * quantity),
      carbs_g: round1(it.carbs_g * quantity),
      fat_g: round1(it.fat_g * quantity),
      fiber_g: it.fiber_g != null ? round1(it.fiber_g * quantity) : undefined,
      // Micronutrients scale with the macros — they're per-portion too.
      nutrients: scaleNutrients(it.nutrients, quantity)
    })),
    totals: {
      calories: round1(estimate.totals.calories * quantity),
      protein_g: round1(estimate.totals.protein_g * quantity),
      carbs_g: round1(estimate.totals.carbs_g * quantity),
      fat_g: round1(estimate.totals.fat_g * quantity),
      fiber_g: round1(estimate.totals.fiber_g * quantity)
    }
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function sumTotals(meals: LoggedMeal[]): MealEstimate["totals"] {
  return meals.reduce(
    (acc, m) => ({
      calories: acc.calories + m.estimate.totals.calories,
      protein_g: acc.protein_g + m.estimate.totals.protein_g,
      carbs_g: acc.carbs_g + m.estimate.totals.carbs_g,
      fat_g: acc.fat_g + m.estimate.totals.fat_g,
      fiber_g: acc.fiber_g + m.estimate.totals.fiber_g
    }),
    emptyTotals()
  );
}
